import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { SortDirection } from '@angular/material/sort';
import * as _ from 'lodash';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, skip, tap } from 'rxjs/operators';

import { CuttingMatBaseGroup, TypeGroupMaterial } from '../../models/cutting/cutting-material-group.model';
import { CuttingMaterial, MaterialType } from '../../models/cutting/cutting-material.model';
import { ApiLinks } from '../cutting-panel-list/csl.api-links';
import { CuttingMatBaseDialogService } from './cutting-material-base-dialog.service';

type DataModifiers = [string, [number, number], { active: string; direction: SortDirection }, PageEvent];

export interface CuttingMaterialBaseDialogData {
  materialsToExclude?: CuttingMaterial[];
  multiple?: boolean;
  showPrices?: boolean;
  currencyName?: string;
  dataProvider?: CuttingMatBaseDialogService;
  showQuantityInStock?: boolean;
  forPanel?: boolean;
}

@Component({
  selector: 'csl-material-base',
  templateUrl: 'cutting-material-base-dialog.component.html',
  styleUrls: ['cutting-material-base-dialog.component.scss'],
})
export class CuttingMaterialBaseDialogComponent implements OnInit {
  dataSource: MatTableDataSource<CuttingMaterial> | null = new MatTableDataSource();
  fixedType: MaterialType = undefined;
  displayedColumns = ['select', 'texture', 'name', 'article', 'price', 'quantityInStock'];

  find$: BehaviorSubject<string> = new BehaviorSubject('');
  priceFilter$: BehaviorSubject<[number, number]> = new BehaviorSubject([null, null]);
  modifiers$: Observable<DataModifiers>;
  sort$: BehaviorSubject<{ active: string; direction: SortDirection }> = new BehaviorSubject({
    active: 'price',
    direction: '',
  });
  page$: BehaviorSubject<PageEvent> = new BehaviorSubject({ pageIndex: 0, pageSize: 25, length: 0 });

  dialogData: CuttingMaterialBaseDialogData = {
    materialsToExclude: undefined,
    multiple: true,
    showPrices: false,
    currencyName: '',
    forPanel: false,
  };

  currentGroupId = 0;
  focusedGroupId = -1;
  materials: CuttingMaterial[];

  selectedMaterials: CuttingMaterial[] = [];

  groups$: Observable<CuttingMatBaseGroup[]>;
  materials$: Observable<CuttingMaterial[]>;

  totalCount = 0;
  constructor(
    public dialogRef: MatDialogRef<CuttingMaterialBaseDialogComponent>,
    public materialBaseService: CuttingMatBaseDialogService,
    public dialog: MatDialog,

    @Inject(MAT_DIALOG_DATA) private data: CuttingMaterialBaseDialogData
  ) {
    this.dialogData = _.merge(this.dialogData, data);

    if (!this.dialogData.showPrices) {
      _.pull(this.displayedColumns, 'price');
    }

    if (!this.dialogData.showQuantityInStock) {
      _.pull(this.displayedColumns, 'quantityInStock');
    }
  }

  ngOnInit(): void {
    this.initListners();
    this.getMaterialGroups();
  }

  onGroupSelect(group: CuttingMatBaseGroup): void {
    this.currentGroupId = group.id;
    this.dataSource.data = [];
    this.page$.next(this.page$.value);
  }

  private initListners(): void {
    this.modifiers$ = combineLatest([this.find$, this.priceFilter$, this.sort$, this.page$]).pipe(
      skip(1),
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => {
        this.dataSource.data = [];
        this.makeMaterialsReq();
      })
    );
  }

  private makeMaterialsReq(): void {
    this.materials$ = this.getMaterials();
  }

  private getMaterials(): Observable<CuttingMaterial[]> {
    const filterText = this.find$.value.replace(/'/g, '');

    const dataProvider = this.data.dataProvider || this.materialBaseService;

    return dataProvider
      .getMaterials(
        this.currentGroupId,
        this.page$.value.pageSize,
        this.page$.value.pageIndex,
        filterText,
        this.priceFilter$.value[0],
        this.priceFilter$.value[1],
        this.sort$.value.direction,
        this.data.forPanel
      )
      .pipe(
        tap(res => (this.totalCount = res.totalCount)),
        map(res => res.data),
        tap(res => (this.dataSource.data = res))
      );
  }

  getMaterialGroups(): void {
    const dataProvider = this.data.dataProvider || this.materialBaseService;

    this.groups$ = dataProvider.getGroups([TypeGroupMaterial.Linear, TypeGroupMaterial.Plate], this.data.forPanel).pipe(
      tap(res => {
        if (res?.length) {
          this.focusedGroupId = res[0].id;
        }
      })
    );
  }

  toggleSelection(item: CuttingMaterial): void {
    if (this.isExcludeExists(item)) {
      return;
    }

    const itemIndex = this.selectedMaterials.findIndex(
      (it: CuttingMaterial) =>
        it.name.toLowerCase().trim() === item.name.toLowerCase().trim() &&
        it.article.toLowerCase().trim() === item.article.toLowerCase().trim()
    );

    if (itemIndex !== -1) {
      this.selectedMaterials.splice(itemIndex, 1);
    } else {
      if (!this.dialogData.multiple) {
        this.selectedMaterials = [];
      }

      // FIXME: Crutch
      const material = _.cloneDeep(item);
      material.inMaterialBaseId = material.id;
      material.id = 0;

      this.selectedMaterials.push(material);
    }
  }

  saveAndClose(): void {
    this.dialogRef.close(this.selectedMaterials);
  }

  isSelected(item: CuttingMaterial): boolean {
    return this.selectedMaterials.some(
      it =>
        it.name.toLowerCase().trim() === item.name.toLowerCase().trim() &&
        it.article.toLowerCase().trim() === item.article.toLowerCase().trim()
    );
  }

  isExcludeExists(item: CuttingMaterial): boolean {
    return this.dialogData.materialsToExclude.some(
      m =>
        m.name.toLowerCase().trim() === item.name.toLowerCase().trim() &&
        m.article.toLowerCase().trim() === item.article.toLowerCase().trim()
    );
  }

  showFullNameTooltip(el: HTMLElement): boolean {
    if (el.offsetWidth < el.scrollWidth) {
      return true;
    } else {
      return false;
    }
  }
}
